import { WorkStatusSchema } from '@kaiizen/kai-scan-template-system';
import { boolean, number, optional, string, z, zenum, zodLess, ZodLessInfer, zrecord } from '@kaiizen/zod-less';

/**
 * Schema for overview data in scope reports
 * Contains property details, project details, scan details, and cost summary
 */
export const ScopeReportOverviewDataSchema = z.object({
  // Property Details
  propertyId: optional(string),
  propertyAddress: optional(string),
  beds: optional(number),
  baths: optional(number),
  squareFeet: optional(number),
  acres: optional(number),
  vintage: optional(number),
  hasGarage: optional(boolean),
  stories: optional(number),

  // Project Details
  projectName: optional(string),
  projectStatus: optional(string),
  projectEstimatedCompletionDate: optional(string),

  // Scan Details
  scanName: optional(string),
  scanId: optional(string),
  scanType: optional(string),
  approvedDate: optional(string),
  generalContractor: optional(string),
  responsibleOrg: optional(string),

  // Cost Summary
  totalMaterialCost: optional(number),
  totalLaborCost: optional(number),
  totalProjectCost: optional(number),
  projectBudget: optional(number),

  // Codes
  doorCode: optional(string),
  gateCode: optional(string),

  // Notes filed on the whole scope (scanPartId = the scan root), newest first
  scopeNotes: optional([string]),
});

export type ScopeReportOverview = ZodLessInfer<typeof ScopeReportOverviewDataSchema>;

/**
 * Schema for task photo in scope reports
 */
export const ScopeReportTaskPhotoSchema = z.object({
  id: string,
  dataUrl: string,
  createdAt: optional(string),
});

/**
 * Schema for a single line item in the scope report
 */
export const ScopeReportLineItemSchema = z.object({
  groupId: string,
  groupTemplateId: string,
  groupName: string,
  groupRoomArea: optional(number),
  groupNotes: optional([string]),
  taskId: string,
  taskTemplateId: string,
  taskName: string,
  taskDescription: optional(string),
  taskWorkStatus: optional(WorkStatusSchema),
  taskNotes: optional([string]),
  trade: optional(string),
  optionId: string,
  optionTemplateId: string,
  optionName: string,
  lineItemName: string,
  lineItemId: string,
  lineItemTemplateId: string,
  isDeferredLineItem: optional(boolean),
  isTenantRespLineItem: optional(boolean),
  /**
   * Every modifier tag the line item carried when the report was built (KAIDEV-4206).
   *
   * The two booleans above cannot express a modifier an org minted for itself, and they stay for
   * the financial rules and change-order comparisons that are still written in terms of them.
   *
   * Optional because not every producer sets it, not for back-compatibility: no
   * `ScopeReportLineItem` is persisted or transported anywhere. Every one is built on demand -
   * a client memo over the live scan, kaiPro's own hook, or server-side PDF and Excel generation
   * - so there is no stored row from before this field existed. Two producers do leave it unset:
   * the legacy status-task branch inside `buildScopeReportLineItems`, and
   * `buildScopeReportLineItemsForBilling`, which builds its own rows for Excel. A reader's
   * `?? [booleans]` fallback is therefore reached, on every status-task row (review rounds 1
   * and 2).
   */
  modifierTags: optional([string]),
  isEditRequest: optional(boolean),
  isEditResolution: optional(boolean),
  isAdminEditResolution: optional(boolean),
  lineItemNotes: optional([string]),
  contractorName: string,
  glCode: optional(string),
  scopePDFTaskDescName: optional(string),
  unitPrice: number,
  totalLineItemCost: number,
  yardiDescription: optional(string),
  productName: optional(string.nullable()),
  productSku: optional(string.nullable()),
  productBrand: optional(string.nullable()),
  productExists: optional(boolean),
  productQuantity: optional(number),
  taskPhotos: optional(z.array(ScopeReportTaskPhotoSchema)),
  productQuantityValueType: string,
  // Status task fields
  isStatusTask: optional(boolean),
  isOtherTask: optional(boolean), // non-standard task: scopePDFTaskDescName holds its written description
  isPunchlistTask: optional(boolean),
  statusValue: optional(string),
  statusLabel: optional(string),
});

export type ScopeReportLineItem = ZodLessInfer<typeof ScopeReportLineItemSchema>;

/**
 * Schema for grouped report line items
 * Maps group names to arrays of line items
 */
export const GroupedReportLineItemSchema = zrecord(z.array(ScopeReportLineItemSchema));

/**
 * Schema for contractor report line items
 * Maps contractor names to arrays of line items
 */
export const ContractorReportLineItemSchema = zrecord(z.array(ScopeReportLineItemSchema));

/**
 * Schema for contractor assignment info (trade-based mapping)
 */
export const ContractorAssignmentSchema = z.object({
  trade: string,
  name: string,
});

/**
 * Schema for assigned contractor with trades (contractor-based mapping)
 */
export const AssignedContractorSchema = z.object({
  name: string,
  trades: z.array(string),
});

/**
 * Schema for org settings included in scope report data
 */
export const ScopeReportOrgSettingsSchema = z.object({
  logoSvg: optional(string),
  includeTaskPhotos: z.boolean(),
});

/**
 * Schema for the complete scope report export data
 * groupIdSowData is optional for backward compatibility with clients that do not expect it.
 */
export const ScopeReportDataSchema = z.object({
  overviewData: ScopeReportOverviewDataSchema,
  /** @deprecated Use groupIdSowData instead (keys are stable group IDs). */
  groupSowData: GroupedReportLineItemSchema.describe(
    'Deprecated. Use groupIdSowData instead (keys are stable group IDs).',
  ),
  groupIdSowData: optional(GroupedReportLineItemSchema),
  assignedContractorsWithTrades: zrecord(AssignedContractorSchema),
  orgSettings: ScopeReportOrgSettingsSchema,
});

/**
 * Schema for scope report change type (change order / diff semantics).
 * Single source of truth for change type values in scope reports.
 */
export const ScopeReportChangeTypeSchema = zenum(
  'existing',
  'new',
  'deleted',
  'reversal',
  'restatement',
  'modified',
);
export type ScopeReportChangeType = ZodLessInfer<typeof ScopeReportChangeTypeSchema>;

/**
 * Schema for change order line item
 * Extends ScopeReportLineItem with change tracking metadata
 */
export const ChangeOrderLineItemSchema = ScopeReportLineItemSchema.extend({
  baselineTotalPrice: optional(number),
  baselineContractorName: optional(string),
  baselineQuantity: optional(number),
  baselineUnitPrice: optional(number),
  changeType: ScopeReportChangeTypeSchema,
  changedColumns: z.object({
    contractor: z.boolean(),
    quantity: z.boolean(),
    rate: z.boolean(),
    amount: z.boolean(),
    deferred: z.boolean(),
    tenantResponsibility: z.boolean(),
  }),
});

/**
 * Schema for group change order
 * Groups all changed line items for a specific group
 */
export const GroupChangeOrderSchema = z.object({
  groupId: optional(string),
  groupName: string,
  baselineCost: number,
  newCost: number,
  lineItems: z.array(ChangeOrderLineItemSchema),
});

/**
 * Schema for change request diff report overview data
 */
export const ChangeRequestDiffReportOverviewDataSchema = z.object({
  currentTotalCost: number,
  baselineTotalCost: number,
  totalDifference: number,
});

/**
 * Schema for change request diff report data
 * Contains comparison between a change request scan and its baseline
 */
export const ChangeRequestDiffReportDataSchema = zodLess({
  overviewData: ChangeRequestDiffReportOverviewDataSchema,
  groupChangeOrders: [GroupChangeOrderSchema],
});
export type ChangeRequestDiffReportData = ZodLessInfer<typeof ChangeRequestDiffReportDataSchema>;

/**
 * Schema for change order data attached to a line item
 * Contains baseline values and change tracking metadata
 */
export const LineItemChangeOrderDataSchema = z.object({
  baselineContractorName: optional(string),
  baselineQuantity: optional(number),
  baselineUnitPrice: optional(number),
  baselineTotalPrice: optional(number),
  changedColumns: z.object({
    contractor: z.boolean(),
    quantity: z.boolean(),
    rate: z.boolean(),
    amount: z.boolean(),
    deferred: z.boolean(),
    tenantResponsibility: z.boolean(),
  }),
  changeType: optional(ScopeReportChangeTypeSchema),
});

/**
 * Schema for a scope report line item with optional change order data
 * Used when computing diffs between current and baseline SOW data
 */
export const ScopeReportLineItemWithChangeDataSchema = ScopeReportLineItemSchema.extend({
  changeOrderData: optional(LineItemChangeOrderDataSchema),
});

export type ScopeReportLineItemWithChangeData = ZodLessInfer<typeof ScopeReportLineItemWithChangeDataSchema>;

/**
 * Schema for change request diff report data
 * Contains comparison between a change request scan and its baseline
 */
export const ChangeRequestDiffReportDataV2 = zodLess({
  overviewData: ChangeRequestDiffReportOverviewDataSchema,
  scopeLineItems: [ScopeReportLineItemWithChangeDataSchema],
});
export type ChangeRequestDiffReportDataV2 = ZodLessInfer<typeof ChangeRequestDiffReportDataV2>;
